import { useEffect, useState, type JSX } from "react";
import { CpsButtonText } from "corpus";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import {
  createFileRoute,
  useNavigate,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import { RecipientEmptyStateWrapperQueryDocument } from "@repo/graphql-types/graphql";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import type { Info } from "@/components/ui/list/item";
import { InformationalList } from "@/components/ui/list/informational-list";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import {
  createBankAccountFormAtom,
  isBankAccountFormConcluded,
} from "@/lib/atoms/create-bank-account-atom";
import { Button } from "@/components/ui/button/button";
import { Sentry } from "@/lib/utils/sentry";
import { trackEvent } from "@/lib/tracking";
import { usePayAccountCreationState } from "@/hooks/use-pay-account-creation-state";

const GetUserRecipientDataQuery = graphql(/* GraphQL */ `
  query GetUserRecipientData($codUsuario: Int!, $codClinica: Int, $skipClinica: Boolean!) {
    usuarios(where: { codUsuario: { _eq: $codUsuario } }) {
      codUsuario
      nome
      sobrenome
      cpf
      email
      dataNascimento
    }
    usuariosProfissionais(where: { codUsuario: { _eq: $codUsuario } }) {
      celularPessoalMembro
      profissao
    }
    usuariosEspecialidades(where: { codUsuario: { _eq: $codUsuario } }, limit: 1) {
      Especialidade {
        nome
      }
    }
    usuariosEnderecos: tbUsuariosEnderecos(
      where: { codUsuario: { _eq: $codUsuario } }
      limit: 1
    ) {
      logradouro
      numero
      bairro
      cidade
      uf
      cep
      complemento
    }
    clinicas: tbClinicas(where: { codClinica: { _eq: $codClinica } }) @skip(if: $skipClinica) {
      codClinica
      razaoSocial
      cnpj
      nomeResponsavel
      cpfResponsavel
      emailResponsavel
      celularResponsavel
      dataNascimentoResponsavel
      profissaoResponsavel
    }
    clinicasEnderecos: tbClinicasEnderecos(
      where: { codClinica: { _eq: $codClinica } }
      limit: 1
    ) @skip(if: $skipClinica) {
      logradouro
      numero
      bairro
      cidade
      uf
      cep
      complemento
    }
    clinicaEnderecoResponsavel: tbClinicasEnderecosResponsaveis(
      where: { codClinica: { _eq: $codClinica } }
      limit: 1
    ) @skip(if: $skipClinica) {
      logradouro
      numero
      bairro
      cidade
      uf
      cep
      complemento
    }
  }
`);

const ExpensumCreateRecipientMutation = graphql(/* GraphQL */ `
  mutation ExpensumCreateRecipient($arg1: ExpensumCreateRecipientInput!) {
    ExpensumCreateRecipient(arg1: $arg1) {
      pagarme_recipient_id
      message
    }
  }
`);

const ConfirmBankAccountPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const { user } = useRouteContext({ from: "__root__" });
  const [atomValues] = useAtom(createBankAccountFormAtom);
  const resetBankAccountForm = useResetAtom(createBankAccountFormAtom);

  // Usar newClinicaId do atom se disponível (para usuários PF que criaram PJ no fluxo)
  const codClinicaToUse = atomValues.newClinicaId ?? user.codClinica;

  const { data: userData, isLoading: userDataLoading } = useGraphQL(
    GetUserRecipientDataQuery,
    {
      codUsuario: user.codUsuario,
      codClinica: codClinicaToUse ?? undefined,
      skipClinica: !codClinicaToUse,
    },
  );

  const { mutateAsync, isPending: isCreatingRecipient } =
    useGraphQLMutationWithErrorHandler(ExpensumCreateRecipientMutation);

  const { markCreationCompleted } = usePayAccountCreationState();
  const [isNavigatingToStatus, setIsNavigatingToStatus] = useState(false);

  const invalidateRecipientEmptyStateWrapper = useInvalidateQuery(
    RecipientEmptyStateWrapperQueryDocument,
  );

  useEffect(() => {
    // Não redirecionar se estamos navegando para a tela de status
    if (!isNavigatingToStatus && !isBankAccountFormConcluded(atomValues)) {
      void navigate({ to: "/settings/payments" });
    }
  }, [atomValues, navigate, isNavigatingToStatus]);

  const handleConfirm = async (): Promise<void> => {
    if (!userData) {
      return;
    }

    try {
      const isCompany = atomValues.holderType === "company";

      const usuario = userData.usuarios[0];
      const profissional = userData.usuariosProfissionais[0];
      const enderecoUsuario = userData.usuariosEnderecos[0];
      const clinica = userData.clinicas?.[0];
      const enderecoClinica = userData.clinicasEnderecos?.[0];
      const enderecoResponsavel =
        userData.clinicaEnderecoResponsavel?.[0] ?? enderecoClinica;

      const endereco = isCompany ? enderecoClinica : enderecoUsuario;

      if (!endereco) {
        return;
      }

      if (!profissional.celularPessoalMembro) {
        return;
      }

      const responsavelLegal =
        isCompany && clinica
          ? {
              nome: clinica.nomeResponsavel ?? "",
              email: clinica.emailResponsavel ?? "",
              cpf: clinica.cpfResponsavel ?? "",
              dataNascimento: clinica.dataNascimentoResponsavel ?? "",
              rendaMensal: 10000,
              ocupacaoProfissional:
                clinica.profissaoResponsavel ?? "Profissional de Saúde",
              numeroTelefone: clinica.celularResponsavel ?? "",
              endereco: {
                rua: enderecoResponsavel?.logradouro ?? "",
                numero: enderecoResponsavel?.numero ?? "",
                bairro: enderecoResponsavel?.bairro ?? "",
                cidade: enderecoResponsavel?.cidade ?? "",
                estado: enderecoResponsavel?.uf ?? "",
                cep: enderecoResponsavel?.cep ?? "",
                complemento: enderecoResponsavel?.complemento ?? undefined,
              },
            }
          : undefined;

      const companySpecificFields = isCompany
        ? {
            nome: clinica?.razaoSocial,
            razaoSocial: clinica?.razaoSocial,
            rendaAnual: 120000,
          }
        : {};

      const especialidadeNome = userData.usuariosEspecialidades[0]?.Especialidade?.nome;

      const individualSpecificFields = !isCompany
        ? {
            nome: `${usuario.nome} ${usuario.sobrenome}`,
            dataNascimento: usuario.dataNascimento,
            rendaMensal: 10000,
            ocupacaoProfissional: especialidadeNome ?? "Profissional de Saúde",
          }
        : {};

      const recipientData = {
        codClinica: isCompany ? codClinicaToUse : undefined,
        codUsuario: user.codUsuario,
        code: isCompany ? `c_${codClinicaToUse}` : `u_${user.codUsuario}`,
        email: usuario.email,
        documento: atomValues.holderDocument,
        tipo: isCompany ? "corporation" : "individual",
        numeroTelefone: profissional.celularPessoalMembro,
        endereco: {
          rua: endereco.logradouro ?? "",
          numero: endereco.numero ?? "",
          bairro: endereco.bairro ?? "",
          cidade: endereco.cidade ?? "",
          estado: endereco.uf ?? "",
          cep: endereco.cep ?? "",
          complemento: endereco.complemento ?? undefined,
        },
        responsavelLegal,
        contaBancaria: {
          tipoDeTitular: atomValues.holderType,
          codigoBanco: atomValues.bank,
          numeroAgencia: atomValues.branchNumber,
          digitoVerificadorAgencia: atomValues.branchCheckDigit ?? "",
          numeroConta: atomValues.accountNumber,
          digitoVerificadorConta: atomValues.accountCheckDigit,
          tipoConta: atomValues.bankAccountType,
        },
        ...companySpecificFields,
        ...individualSpecificFields,
      };

      const result = await mutateAsync({
        arg1: {
          codClinica: codClinicaToUse,
          recipientData,
        },
      });

      // Verificar se o recipient foi criado com sucesso
      if (result.ExpensumCreateRecipient) {
        const { pagarme_recipient_id: pagarmeRecipientId, message } =
          result.ExpensumCreateRecipient;

        resetBankAccountForm();
        invalidateRecipientEmptyStateWrapper();

        if (pagarmeRecipientId && message === "active") {
          markCreationCompleted();

          trackEvent("Conta recebedora criada", {
            recipientId: pagarmeRecipientId,
          });

          setIsNavigatingToStatus(true);
          void navigate({
            to: "/settings/payments/create-recipient/status",
            search: { status: "success" },
          });
        } else if (pagarmeRecipientId) {
          // Recipient criado mas com status pendente
          markCreationCompleted();

          setIsNavigatingToStatus(true);
          void navigate({
            to: "/settings/payments/create-recipient/status",
            search: { status: "pending" },
          });
        } else {
          // Erro ao criar recipient
          setIsNavigatingToStatus(true);
          void navigate({
            to: "/settings/payments/create-recipient/status",
            search: { status: "error" },
          });
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: "create-recipient",
        },
      });
    }
  };

  const bankAccountTypeLabel =
    atomValues.bankAccountType === "checking" ? "Conta Corrente" : "Conta Poupança";

  const bankInfos: Info[] = [
    { label: "Titular", value: atomValues.holderName },
    { label: "Banco", value: `${atomValues.bankName} ${atomValues.bank}` },
    {
      label: "Agência",
      value: atomValues.branchCheckDigit
        ? `${atomValues.branchNumber}-${atomValues.branchCheckDigit}`
        : atomValues.branchNumber,
    },
    {
      label: "Conta",
      value: `${atomValues.accountNumber}-${atomValues.accountCheckDigit}`,
    },
    { label: "Tipo", value: bankAccountTypeLabel },
  ];

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Cadastrar conta" align="center" />
      </HeaderRoot>
      <Page className="px-6 py-8">
        <div className="flex flex-col gap-8">
          <h1 className="text-xl text-neutral-600 font-semibold w-full">
            Confira se os seus dados estão corretos.
          </h1>

          <InformationalList infos={bankInfos} />

          <div className="flex flex-col gap-4">
            <Button
              fullWidth
              onClick={() => void handleConfirm()}
              loading={userDataLoading || isCreatingRecipient}
            >
              Confirmar
            </Button>
            <CpsButtonText onClick={() => router.history.back()}>
              Editar dados
            </CpsButtonText>
          </div>
        </div>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/payments/create-recipient/confirm-bank-account",
)({
  component: ConfirmBankAccountPage,
});
